/**
 * Admin API client. Two ways to be an admin, mirroring the API:
 *  - a Google sign-in whose account is on the API's ADMIN_EMAILS list → session
 *    token sent as `Authorization: Bearer`
 *  - the admin key from SSM → `x-admin-key` (kept for scripts and as a fallback)
 * Both live in sessionStorage: this tab only, gone when it closes.
 */

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";
export const CDN_BASE = (import.meta.env.VITE_CDN_BASE_URL as string | undefined) ?? "";
export const GOOGLE_WEB_CLIENT_ID = (import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID as string | undefined) ?? "";

/** The categories every chain is filed under: backend/src/types.ts TOPICS. Keep them in step. */
export const TOPICS = ["mechanisms", "body", "food", "earth", "space", "energy", "computing", "materials", "life", "home", "infrastructure", "sound", "perception"];

const KEY = "brainstorm.adminKey";
const SESSION = "brainstorm.session";
const WHO = "brainstorm.who";

export const getKey = (): string => sessionStorage.getItem(KEY) ?? "";
export const setKey = (k: string): void => { k ? sessionStorage.setItem(KEY, k) : sessionStorage.removeItem(KEY); };
export const getSession = (): string => sessionStorage.getItem(SESSION) ?? "";
export const setSession = (t: string, who = ""): void => {
  t ? sessionStorage.setItem(SESSION, t) : sessionStorage.removeItem(SESSION);
  who ? sessionStorage.setItem(WHO, who) : sessionStorage.removeItem(WHO);
};
export const whoAmI = (): string => sessionStorage.getItem(WHO) ?? (getKey() ? "admin key" : "");
export const signedIn = (): boolean => !!(getSession() || getKey());
export const signOut = (): void => { setKey(""); setSession(""); };

export class ApiError extends Error {
  constructor(public status: number, message: string, public issues?: Issue[]) { super(message); }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  if (!API_BASE) throw new ApiError(0, "VITE_API_BASE_URL is not set");
  const headers: Record<string, string> = { "content-type": "application/json" };
  const session = getSession(), key = getKey();
  if (session) headers.authorization = `Bearer ${session}`;
  if (key) headers["x-admin-key"] = key;
  const res = await fetch(`${API_BASE}${path}`, {
    method: init.method ?? "GET",
    headers,
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!res.ok) throw new ApiError(res.status, String(json.error ?? res.statusText), json.issues as Issue[] | undefined);
  return json as T;
}

/** Exchanges a Google ID token for a session; only admins get to keep it. */
export async function signInWithGoogle(idToken: string): Promise<{ email: string; isAdmin: boolean }> {
  const r = await api<{ token: string; user: { email?: string; name?: string }; isAdmin: boolean }>("/auth/google", { method: "POST", body: { idToken } });
  const email = r.user.email ?? r.user.name ?? "you";
  if (r.isAdmin) setSession(r.token, email);
  return { email, isAdmin: r.isAdmin };
}

// --- Shapes ------------------------------------------------------------

export interface Issue { severity: "error" | "warning"; path: string; message: string }
export interface Validation { ok: boolean; issues: Issue[]; errors: Issue[]; warnings: Issue[] }
export interface Published { published: number; removed: number; at: string }

export interface Stats {
  days: number; byType: Record<string, number>;
  byDay: Array<{ day: string; counts: Record<string, number>; visitors: number }>;
  bySpread: Array<{ spreadId: string; views: number; opens: number; completes: number; openRate: number }>;
  visitors: number; sessions: number;
}
export interface Overview {
  stage: string; version: string; published: number; admins: string[];
  spreads: { total: number; approved: number; withErrors: number };
  overrides: { pinned: string[]; hidden: string[] };
  stats: Stats;
}
export interface SpreadMeta {
  id: string; status: "draft" | "needs-work" | "approved";
  createdAt: string; updatedAt: string; updatedBy: string; version: number;
  publishedAt?: string; reviewedBy?: string; reviewedAt?: string; errors: number; warnings: number;
}
export interface SpreadRow extends SpreadMeta {
  title: string; hook: string; topic: string; kind: string; difficulty: number;
  /** false: approved but held back, not published. */
  tags: string[]; inFeed: boolean;
  words: number; beats: number; figures: number;
}
export interface VersionInfo { at: string; status: string; by: string; note?: string; errors: number }
export interface UserRow {
  sub: string; email?: string; name?: string; picture?: string; createdAt: string; lastSeenAt: string;
  prefs: { topics: string[]; dailyGoal?: number; activeDays?: string[] }; saved: number; seen: number;
}
export interface UserItem { spreadId: string; saved?: boolean; liked?: boolean; seenAt?: string; savedAt?: string; updatedAt: string }

export const fmtTime = (iso?: string): string => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");
